<template>
  <div>
    <div class="mb-10">
      <div class="d-flex align-center mb-4">
        <h5 class="mr-2">Requests</h5>
        To: InstallCodes@so.energy
        <SoCopyToClipboard plain-text="InstallCodes@so.energy" inline />
      </div>

      <div class="d-flex align-center gap-2 mb-2">
        <SoButton
          v-if="!installCodeRow.installCode"
          title="Request Install Codes"
          prepend-icon="email"
          data-testid="so-copy-btn"
          @click="requestInstallCodes"
        />
        <SoButton
          v-else-if="installCodeRow.getAsnStatus() === 'missing'"
          title="Raise ASN Update Request"
          prepend-icon="email"
          data-testid="so-copy-btn"
          @click="requestAsnUpdate"
        />
        <SoButton
          title="Data Update Request"
          prepend-icon="email"
          data-testid="so-copy-btn"
          @click="requestDataUpdate"
        />
      </div>
    </div>

    <InstallCodesEmailPreview
      :model-value="!!emailSelected"
      :subject="subjectSelected"
      :email-template="emailSelected"
      @update:model-value="() => (emailSelected = '')"
    />
  </div>
</template>

<script lang="ts">
  import { PropType, defineComponent } from "vue"
  import { formatDateDashed } from "@/helpers/dateHelper"
  import { IInstallCode } from "@/models/installCode/installCode.types"
  import SoButton from "@/components/formControls/SoButton.vue"
  import dayjs from "dayjs"
  import SoCopyToClipboard from "@/components/SoCopyToClipboard.vue"
  import InstallCodesEmailPreview from "./InstallCodesEmailPreview.vue"

  export default defineComponent({
    components: {
      SoButton,
      SoCopyToClipboard,
      InstallCodesEmailPreview,
    },
    props: {
      installCodeRow: {
        type: Object as PropType<IInstallCode>,
        required: true,
      },
    },
    data() {
      return {
        subjectSelected: "",
        emailSelected: "",
      }
    },
    computed: {
      requestInstallCodesEmailTemplate() {
        const { mpxn, msn, deviceId, mapName, mapId } = this.installCodeRow

        const requestedDate = formatDateDashed(dayjs())

        return `Hi Team,

Please can an Install Code be provided for the below meter.

MPXN: ${mpxn ?? " - "}
MSN: ${msn}
Device ID: ${deviceId ?? " - "}
MAP Name: ${mapName ?? " - "}
MAP ID: ${mapId ?? " - "}
Requested Date: ${requestedDate ?? " - "}

Reason: Install Code currently missing and required for appointment.

Thanks`
      },
      requestAsnUpdateTemplate() {
        const { mpxn, msn, deviceId, installCode, mapName, mapId } =
          this.installCodeRow

        return `Hi Team,

Please can an ASN be uploaded for the below meter.

MPXN: ${mpxn ?? " - "}
MSN: ${msn}
Install Code: ${installCode ?? " - "}
Device ID: ${deviceId ?? " - "}
MAP Name: ${mapName ?? " - "}
MAP ID: ${mapId ?? " - "}

Reason: ASN currently not uploaded and required for install.

Thanks`
      },
      requestDataUpdateTemplate() {
        const { mpxn, msn, deviceId, mapId } = this.installCodeRow

        return `Hi Team,

Please can the following data be checked/updated for the below meter.

MPXN: ${mpxn ?? " - "}
MSN: ${msn}
Device ID: ${deviceId ?? " - "}
MAP ID: ${mapId ?? " - "}

Reason: Data requires review/update.

Thanks`
      },
    },
    methods: {
      async requestInstallCodes() {
        if (!this.installCodeRow) return

        this.subjectSelected = `Install Code Request - MSN ${this.installCodeRow.msn}`
        this.emailSelected = this.requestInstallCodesEmailTemplate

        const to = "InstallCodes@so.energy"
        const subject = `Install Code Request - MSN ${this.installCodeRow.msn}`

        window.location.href = `mailto:${to}?subject=${encodeURIComponent(
          subject,
        )}&body=${encodeURIComponent(this.requestInstallCodesEmailTemplate)}`
      },
      async requestAsnUpdate() {
        if (!this.installCodeRow) return

        this.subjectSelected = `ASN Upload Request - MSN ${this.installCodeRow.msn}`
        this.emailSelected = this.requestAsnUpdateTemplate

        const to = "InstallCodes@so.energy"
        const subject = `ASN Upload Request - MSN ${this.installCodeRow.msn}`

        window.location.href = `mailto:${to}?subject=${encodeURIComponent(
          subject,
        )}&body=${encodeURIComponent(this.requestAsnUpdateTemplate)}`
      },
      async requestDataUpdate() {
        if (!this.installCodeRow) return

        this.subjectSelected = `Data Update Request - MSN ${this.installCodeRow.msn}`
        this.emailSelected = this.requestDataUpdateTemplate

        const to = "InstallCodes@so.energy"
        const subject = `Data Update Request - MSN ${this.installCodeRow.msn}`

        window.location.href = `mailto:${to}?subject=${encodeURIComponent(
          subject,
        )}&body=${encodeURIComponent(this.requestDataUpdateTemplate)}`
      },
    },
  })
</script>
